<script setup lang="ts">
import { computed, ref } from 'vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { useMaatschappijDocumentenGetVerzekeringskaarten } from '@services/documenten-public/orval-api/maatschappij-documenten/maatschappij-documenten';
import { useProductMaatschappijGetAllProductmaatschappijen } from '@services/producten-public/orval-api/product-maatschappij-api/product-maatschappij-api';
import { useVerzekeringskaarten } from '../composables/useVerzekeringskaarten';
import { getDocumentIcon, getDocumentIconColor } from '../utils/document-utils';

const {
  data: verzekeringskaarten,
  error: verzekeringskaartenError,
  isPending: verzekeringskaartenPending,
} = useMaatschappijDocumentenGetVerzekeringskaarten({
  query: { select: (res) => res.result },
});

const {
  data: productenMetMaatschappijen,
  error: productenError,
  isPending: productenPending,
} = useProductMaatschappijGetAllProductmaatschappijen({
  query: { select: (res) => res.result },
});

useWatchErrorHandler(verzekeringskaartenError);
useWatchErrorHandler(productenError);

const isPending = computed(() => verzekeringskaartenPending.value || productenPending.value);

const zoekterm = ref('');

const { gegroepeerdeKaarten, heeftResultaten } = useVerzekeringskaarten(
  verzekeringskaarten,
  productenMetMaatschappijen,
  zoekterm,
);
</script>

<template>
  <div>
    <template v-if="isPending">
      <div class="overflow-hidden rounded-lg border border-grey-200 bg-white">
        <div class="bg-grey-50 p-4">
          <div class="flex gap-4">
            <VSkeleton class="w-full max-w-xs" />
            <VSkeleton class="flex-1" />
          </div>
        </div>
        <div class="space-y-3 divide-y divide-grey-100 p-4">
          <VSkeleton v-for="i in 10" :key="i" height="md" />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="mb-4">
        <VSearch
          v-model="zoekterm"
          placeholder="Zoek verzekeringskaart..."
          class="w-full max-w-xs"
        />
      </div>

      <div
        v-if="heeftResultaten"
        class="overflow-hidden rounded-lg border border-grey-200 bg-white"
      >
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-grey-50 text-left text-xs uppercase tracking-wide text-grey-500">
              <tr>
                <th class="w-px whitespace-nowrap p-4 font-medium">Product</th>
                <th class="w-px whitespace-nowrap p-4 font-medium">Maatschappij</th>
                <th class="p-4 font-medium">Document</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-grey-100">
              <template v-for="groep in gegroepeerdeKaarten" :key="groep.productNaam">
                <template
                  v-for="(maatschappij, mIndex) in groep.maatschappijen"
                  :key="maatschappij.maatschappijNaam"
                >
                  <tr
                    v-for="(doc, dIndex) in maatschappij.documenten"
                    :key="doc.url"
                    class="hover:bg-grey-50"
                  >
                    <td
                      v-if="mIndex === 0 && dIndex === 0"
                      :rowspan="groep.totaalAantalDocumenten"
                      class="w-px whitespace-nowrap border-r border-grey-100 p-4 align-top font-medium text-grey-700"
                    >
                      {{ groep.productNaam }}
                    </td>
                    <td
                      v-if="dIndex === 0"
                      :rowspan="maatschappij.documenten.length"
                      class="w-px whitespace-nowrap border-r border-grey-100 p-4 align-top text-grey-800"
                    >
                      <figure class="flex flex-col items-center gap-1">
                        <img
                          v-if="maatschappij.logoUrl"
                          :src="maatschappij.logoUrl"
                          :alt="maatschappij.maatschappijNaam"
                          class="h-10 max-w-[120px] object-contain"
                        />
                        <figcaption class="text-xs text-grey-500">
                          {{ maatschappij.maatschappijNaam }}
                        </figcaption>
                      </figure>
                    </td>
                    <td class="p-4">
                      <a
                        :href="doc.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        <FontAwesomeIcon
                          :icon="getDocumentIcon(doc.url, doc.bestandsnaam)"
                          :class="getDocumentIconColor(doc.url, doc.bestandsnaam)"
                        />
                        {{ doc.bestandsnaam }}
                      </a>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <p v-if="!heeftResultaten && zoekterm" class="mt-6 text-center text-grey-500">
        Geen verzekeringskaarten gevonden voor "{{ zoekterm }}"
      </p>

      <p v-else-if="!heeftResultaten" class="mt-6 text-center text-grey-500">
        Geen verzekeringskaarten beschikbaar.
      </p>
    </template>
  </div>
</template>
